import { LandingNavbar } from "@/components/landing-navbar";
import { Footer } from "@/components/footer";
import { SEO } from "@/components/SEO";

export default function TermsOfService() {
  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="Terms of Service"
        description="Read ManageClean's Terms of Service. Understand your rights and responsibilities when using our janitorial business management platform."
        keywords={["terms of service", "user agreement", "ManageClean terms"]}
        url="/terms"
      />
      <LandingNavbar />

      {/* Hero Section */}
      <section className="relative overflow-hidden pb-8 pt-32">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_120%,rgba(120,119,198,0.1),rgba(255,255,255,0))]" />
        <div className="container relative z-10 mx-auto px-4">
          <h1 className="mb-4 text-4xl font-bold tracking-tight text-slate-900 md:text-5xl">
            Terms of Service
          </h1>
          <p className="text-slate-600">
            <strong>Effective Date:</strong> December 27, 2025
          </p>
        </div>
      </section>

      {/* Content Section */}
      <section className="py-12">
        <div className="container mx-auto px-4">
          <div className="prose prose-slate prose-lg mx-auto max-w-4xl">
            {/* Introduction */}
            <p>
              Welcome to ManageClean OS. These Terms of Service ("Terms") govern your access to and
              use of ManageClean OS, including our website, platform, and related services
              (collectively, the "Services"). By accessing or using our Services, you agree to be
              bound by these Terms.
            </p>
            <p>
              <strong>Please read these Terms carefully before using our Services.</strong>
            </p>

            {/* Section 1 */}
            <h2 className="mb-4 mt-12 text-2xl font-bold text-slate-900">1. Acceptance of Terms</h2>
            <p>
              By creating an account or using our Services, you acknowledge that you have read,
              understood, and agree to be bound by these Terms and our Privacy Policy. If you do not
              agree to these Terms, you may not access or use our Services.
            </p>
            <p>
              You must be at least 18 years old and have the legal capacity to enter into a binding
              agreement to use our Services. If you are using our Services on behalf of a business
              or organization, you represent that you have the authority to bind that entity to
              these Terms.
            </p>

            {/* Section 2 */}
            <h2 className="mb-4 mt-12 text-2xl font-bold text-slate-900">
              2. Description of Services
            </h2>
            <p>
              ManageClean OS is a business management platform designed for janitorial and cleaning
              service companies. Our Services include:
            </p>
            <ul>
              <li>Client and location management</li>
              <li>Bid creation and proposal generation</li>
              <li>Employee and team management</li>
              <li>Scheduling and calendar tools</li>
              <li>AI-powered document generation and business assistance</li>
              <li>Analytics and reporting</li>
              <li>Marketplace for subcontracting opportunities</li>
            </ul>
            <p>
              We reserve the right to modify, suspend, or discontinue any aspect of our Services at
              any time, with or without notice.
            </p>

            {/* Section 3 */}
            <h2 className="mb-4 mt-12 text-2xl font-bold text-slate-900">
              3. Account Registration
            </h2>
            <p>
              To access most features of our Services, you must create an account. You agree to:
            </p>
            <ul>
              <li>Provide accurate, current, and complete information during registration</li>
              <li>Maintain and promptly update your account information</li>
              <li>Keep your password secure and confidential</li>
              <li>Notify us immediately of any unauthorized use of your account</li>
              <li>Accept responsibility for all activities that occur under your account</li>
            </ul>
            <p>
              We reserve the right to suspend or terminate accounts that violate these Terms or for
              any other reason at our discretion.
            </p>

            {/* Section 4 */}
            <h2 className="mb-4 mt-12 text-2xl font-bold text-slate-900">
              4. Subscription and Payments
            </h2>

            <h3 className="mb-3 mt-6 text-xl font-semibold text-slate-800">Plans and Credits</h3>
            <p>
              Our Services are offered through various subscription plans, each with different
              credit allocations. Credits are used to access AI-powered features. Monthly credits
              reset at the beginning of each billing cycle and do not roll over.
            </p>

            <h3 className="mb-3 mt-6 text-xl font-semibold text-slate-800">Billing</h3>
            <p>
              Subscription fees are billed in advance on a monthly basis. By subscribing to a paid
              plan, you authorize us to charge your payment method for recurring fees until you
              cancel. All payments are processed securely through Stripe.
            </p>

            <h3 className="mb-3 mt-6 text-xl font-semibold text-slate-800">Cancellation</h3>
            <p>
              You may cancel your subscription at any time through your account settings.
              Cancellation will take effect at the end of your current billing period. No refunds
              will be provided for partial months or unused credits.
            </p>

            <h3 className="mb-3 mt-6 text-xl font-semibold text-slate-800">Price Changes</h3>
            <p>
              We reserve the right to change our pricing at any time. Price changes will be
              communicated in advance and will apply to subsequent billing periods.
            </p>

            {/* Section 5 */}
            <h2 className="mb-4 mt-12 text-2xl font-bold text-slate-900">5. Acceptable Use</h2>
            <p>You agree not to use our Services to:</p>
            <ul>
              <li>Violate any applicable laws, regulations, or third-party rights</li>
              <li>Upload or transmit malicious code, viruses, or harmful content</li>
              <li>Attempt to gain unauthorized access to our systems or other users' accounts</li>
              <li>Use automated means to access the Services without our permission</li>
              <li>Interfere with or disrupt the integrity or performance of the Services</li>
              <li>Impersonate any person or entity or misrepresent your affiliation</li>
              <li>Collect or store personal information of other users without consent</li>
              <li>Use our AI features to generate harmful, illegal, or misleading content</li>
            </ul>

            {/* Section 6 */}
            <h2 className="mb-4 mt-12 text-2xl font-bold text-slate-900">6. User Content</h2>
            <p>
              <strong>"User Content"</strong> means any data, information, documents, or materials
              you upload, input, or create using our Services.
            </p>

            <h3 className="mb-3 mt-6 text-xl font-semibold text-slate-800">Ownership</h3>
            <p>
              You retain ownership of your User Content. By using our Services, you grant us a
              limited, non-exclusive license to store, process, and display your User Content solely
              to provide the Services to you.
            </p>

            <h3 className="mb-3 mt-6 text-xl font-semibold text-slate-800">Responsibility</h3>
            <p>
              You are solely responsible for your User Content and the consequences of uploading or
              sharing it. You represent that you have all necessary rights to your User Content and
              that it does not violate any laws or third-party rights.
            </p>

            {/* Section 7 */}
            <h2 className="mb-4 mt-12 text-2xl font-bold text-slate-900">
              7. AI-Generated Content
            </h2>
            <p>
              Our Services include AI-powered features that generate content based on your inputs
              and prompts. Regarding AI-generated content:
            </p>
            <ul>
              <li>
                You are responsible for reviewing and verifying all AI-generated content before use
              </li>
              <li>
                AI-generated content may contain errors, inaccuracies, or inappropriate material
              </li>
              <li>
                We do not guarantee the accuracy, completeness, or suitability of AI-generated
                content
              </li>
              <li>You assume all risks associated with using AI-generated content</li>
            </ul>

            {/* Section 8 */}
            <h2 className="mb-4 mt-12 text-2xl font-bold text-slate-900">
              8. Intellectual Property
            </h2>
            <p>
              The Services, including all software, features, designs, text, graphics, and other
              content provided by ManageClean, are protected by intellectual property laws and are
              owned by or licensed to ManageClean, LLC.
            </p>
            <p>
              You are granted a limited, non-exclusive, non-transferable license to use the Services
              for their intended purpose. You may not copy, modify, distribute, sell, or create
              derivative works based on our Services without our express written permission.
            </p>

            {/* Section 9 */}
            <h2 className="mb-4 mt-12 text-2xl font-bold text-slate-900">
              9. Disclaimer of Warranties
            </h2>
            <p>
              <strong>
                THE SERVICES ARE PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND,
                EITHER EXPRESS OR IMPLIED.
              </strong>{" "}
              To the fullest extent permitted by law, we disclaim all warranties, including implied
              warranties of merchantability, fitness for a particular purpose, and non-infringement.
            </p>
            <p>
              We do not warrant that the Services will be uninterrupted, error-free, secure, or free
              of viruses or other harmful components.
            </p>

            {/* Section 10 */}
            <h2 className="mb-4 mt-12 text-2xl font-bold text-slate-900">
              10. Limitation of Liability
            </h2>
            <p>
              <strong>
                TO THE MAXIMUM EXTENT PERMITTED BY LAW, MANAGECLEAN SHALL NOT BE LIABLE FOR ANY
                INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES
              </strong>
              , including but not limited to loss of profits, data, use, goodwill, or other
              intangible losses, arising out of or relating to your use of or inability to use the
              Services.
            </p>
            <p>
              Our total liability for any claims arising from these Terms or your use of the
              Services shall not exceed the amount you paid to us in the twelve (12) months
              preceding the claim.
            </p>

            {/* Section 11 */}
            <h2 className="mb-4 mt-12 text-2xl font-bold text-slate-900">11. Indemnification</h2>
            <p>
              You agree to indemnify, defend, and hold harmless ManageClean, LLC, its officers,
              directors, employees, agents, and affiliates from and against any claims, liabilities,
              damages, losses, costs, or expenses (including reasonable attorneys' fees) arising
              from:
            </p>
            <ul>
              <li>Your use of the Services</li>
              <li>Your User Content</li>
              <li>Your violation of these Terms</li>
              <li>Your violation of any third-party rights</li>
            </ul>

            {/* Section 12 */}
            <h2 className="mb-4 mt-12 text-2xl font-bold text-slate-900">12. Changes to Terms</h2>
            <p>
              We reserve the right to modify these Terms at any time. We will notify you of material
              changes by posting the updated Terms on our website and updating the "Effective Date."
              Your continued use of the Services after changes take effect constitutes acceptance of
              the revised Terms.
            </p>

            {/* Section 13 */}
            <h2 className="mb-4 mt-12 text-2xl font-bold text-slate-900">
              13. Contact Information
            </h2>
            <p>For questions regarding these Terms, please contact:</p>
            <p className="font-medium">
              ManageClean, LLC
              <br />
              Email: support@manageclean.com
            </p>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
